/**
 * useAutoOptimization Hook
 *
 * Shared hook for auto-optimization logic used across day planning panels.
 * Handles automatic route optimization when routes become stale or are unsorted.
 *
 * Features:
 * - Auto-triggers optimization for 'unsorted' and 'auto_stale' routes
 * - Does NOT auto-optimize manual routes (respects user's manual arrangement)
 * - Handles permission errors with user-friendly toast messages
 * - Prevents double-triggers in React Strict Mode
 *
 * @see docs/features/ROUTE_ORDERING_CLARITY.md
 */

import { useCallback, useEffect, useRef } from 'react';
import { UseMutationResult } from '@tanstack/react-query';
import { toast } from 'sonner';
import { HTTP_STATUS } from '../constants';

/**
 * Routing status as returned by useRoutingKeyStatus hook.
 * Matches RoutingKeyStatus from dayplanning.hooks.ts
 */
export interface RoutingStatus {
  status:
    | 'unsorted'
    | 'auto_valid'
    | 'auto_stale'
    | 'manual_valid'
    | 'manual_stale';
  canAutoSort: boolean;
  canManualEdit: boolean;
  routingTimestamp: Date | null;
}

/**
 * Options for the useAutoOptimization hook
 */
export interface UseAutoOptimizationOptions {
  /** Current routing status from useRoutingKeyStatus */
  routingStatus: RoutingStatus;
  /** The optimize route mutation from useOptimizeRouteMutation */
  optimizeMutation: UseMutationResult<unknown, Error, void, unknown>;
  /** User ID for route operations (optional - disables auto-optimization if not provided) */
  routeUserId: number | undefined;
  /** Number of workorders in the route */
  workorderCount: number;
  /** Whether this is a multi-technician view (disables auto-optimization) */
  isMultiTechView?: boolean;
}

/**
 * Return value from useAutoOptimization hook
 */
export interface UseAutoOptimizationResult {
  /**
   * Handles mutation errors with permission-aware toast messages.
   * Wrap your mutation calls with this for consistent error handling.
   */
  handleMutation: (fn: () => Promise<unknown>) => Promise<void>;
  /**
   * Trigger manual route optimization.
   * Safe to call multiple times - handles loading state internally.
   */
  handleOptimizeRoute: () => void;
  /**
   * Whether auto-optimization will be triggered.
   * Use this to suppress UI alerts when auto-optimize is about to run.
   */
  willAutoOptimize: boolean;
}

/**
 * Hook for managing automatic route optimization.
 *
 * This hook:
 * 1. Determines if routes should be auto-optimized based on status
 * 2. Provides a mutation wrapper with permission-aware error handling
 * 3. Triggers auto-optimization when conditions are met
 * 4. Prevents double-triggers in React Strict Mode
 *
 * @example
 * ```tsx
 * const { handleMutation, handleOptimizeRoute, willAutoOptimize } = useAutoOptimization({
 *   routingStatus,
 *   optimizeMutation,
 *   routeUserId: selectedUserId,
 *   workorderCount: workorders.length,
 * });
 *
 * // Use willAutoOptimize to suppress alerts during auto-optimization
 * <RouteStatusBadge isOptimizing={optimizeMutation.isPending || willAutoOptimize} />
 *
 * // Use handleOptimizeRoute for manual optimization button
 * <Button onClick={handleOptimizeRoute}>Optimize</Button>
 * ```
 */
export function useAutoOptimization({
  routingStatus,
  optimizeMutation,
  routeUserId,
  workorderCount,
  isMultiTechView = false,
}: UseAutoOptimizationOptions): UseAutoOptimizationResult {
  // Wrap mutation to handle permission errors
  const handleMutation = useCallback(async (fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (error: unknown) {
      const err = error as { status?: number; message?: string };
      if (
        err.status === HTTP_STATUS.forbidden ||
        err.message?.toLowerCase().includes('permission')
      ) {
        toast.error('Permission denied', {
          description:
            'You need coordinator access (WB_ADMIN) to edit day planning.',
        });
      } else {
        toast.error('Update failed', {
          description: err.message || 'An unknown error occurred',
        });
      }
    }
  }, []);

  // Handle optimize route action
  const handleOptimizeRoute = useCallback(() => {
    handleMutation(() => optimizeMutation.mutateAsync());
  }, [optimizeMutation, handleMutation]);

  // Auto-optimize when route needs recalculation:
  // - 'unsorted': route has never been calculated
  // - 'auto_stale': route was auto-calculated but is now stale
  // Does NOT auto-optimize manual routes (manual_valid/manual_stale)
  const shouldAutoOptimize =
    routingStatus.status === 'unsorted' ||
    routingStatus.status === 'auto_stale';

  // Calculate if auto-optimize conditions are met (for UI suppression)
  // This is computed BEFORE the effect runs so UI can suppress alerts immediately
  const willAutoOptimize =
    shouldAutoOptimize &&
    !isMultiTechView &&
    !!routeUserId &&
    workorderCount > 0;

  // Ref to prevent double mutation triggers (React Strict Mode, dependency changes)
  const autoOptimizeTriggered = useRef(false);

  // Reset trigger when auto-optimize conditions are no longer met
  useEffect(() => {
    if (!willAutoOptimize) {
      autoOptimizeTriggered.current = false;
    }
  }, [willAutoOptimize]);

  // Auto-trigger optimization when conditions are met
  useEffect(() => {
    if (
      willAutoOptimize &&
      !optimizeMutation.isPending &&
      !autoOptimizeTriggered.current
    ) {
      autoOptimizeTriggered.current = true;
      optimizeMutation.reset();
      handleMutation(() => optimizeMutation.mutateAsync());
    }
  }, [
    willAutoOptimize,
    optimizeMutation.isPending,
    handleMutation,
    optimizeMutation,
  ]);

  return {
    handleMutation,
    handleOptimizeRoute,
    willAutoOptimize,
  };
}
